import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  DatabaseIcon,
} from '@phosphor-icons/react';
import {
  RexelFileDiscoveryJob,
  RexelFileProcessingJob,
  RexelFileDiscoveryJobStatus,
  RexelFileProcessingJobStatus,
} from '@/api/rexel/types';

interface JobStatsCardsProps {
  processingJobs: RexelFileProcessingJob[];
  discoveryJobs: RexelFileDiscoveryJob[];
}

const JobStatsCards: React.FC<JobStatsCardsProps> = ({
  processingJobs,
  discoveryJobs,
}) => {
  // Processing job stats
  const processingStats = {
    total: processingJobs.length,
    completed: processingJobs.filter(
      (job) => job.status === RexelFileProcessingJobStatus.completed,
    ).length,
    running: processingJobs.filter(
      (job) => job.status === RexelFileProcessingJobStatus.running,
    ).length,
    failed: processingJobs.filter(
      (job) => job.status === RexelFileProcessingJobStatus.failed,
    ).length,
    pending: processingJobs.filter(
      (job) => job.status === RexelFileProcessingJobStatus.pending,
    ).length,
  };

  // Discovery job stats
  const discoveryStats = {
    total: discoveryJobs.length,
    completed: discoveryJobs.filter(
      (job) => job.status === RexelFileDiscoveryJobStatus.completed,
    ).length,
    running: discoveryJobs.filter(
      (job) => job.status === RexelFileDiscoveryJobStatus.running,
    ).length,
    failed: discoveryJobs.filter(
      (job) => job.status === RexelFileDiscoveryJobStatus.failed,
    ).length,
  };

  // Calculate total rows processed
  const totalRowsProcessed = processingJobs.reduce((sum, job) => {
    return sum + (job.insertedRows || 0);
  }, 0);

  // Files discovered in recent runs
  const totalFilesDiscovered = discoveryJobs.reduce((sum, job) => {
    return sum + (job.filesDiscovered || 0);
  }, 0);

  const stats = [
    {
      title: 'Processing Jobs',
      value: processingStats.total.toString(),
      icon: DatabaseIcon,
      details: [
        {
          label: 'Completed',
          value: processingStats.completed,
          color: 'text-green-600',
        },
        {
          label: 'Running',
          value: processingStats.running,
          color: 'text-blue-600',
        },
        {
          label: 'Failed',
          value: processingStats.failed,
          color: 'text-red-600',
        },
        {
          label: 'Pending',
          value: processingStats.pending,
          color: 'text-yellow-600',
        },
      ],
    },
    {
      title: 'Discovery Jobs',
      value: discoveryStats.total.toString(),
      icon: MagnifyingGlassIcon,
      details: [
        {
          label: 'Completed',
          value: discoveryStats.completed,
          color: 'text-green-600',
        },
        {
          label: 'Running',
          value: discoveryStats.running,
          color: 'text-blue-600',
        },
        {
          label: 'Failed',
          value: discoveryStats.failed,
          color: 'text-red-600',
        },
      ],
    },
    {
      title: 'Total Rows Processed',
      value: totalRowsProcessed.toLocaleString(),
      icon: CheckCircleIcon,
      details: [],
    },
    {
      title: 'Files Discovered',
      value: totalFilesDiscovered.toLocaleString(),
      icon: MagnifyingGlassIcon,
      details: [],
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <Card key={index}>
          <CardContent className="p-6">
            <div className="flex items-center">
              <stat.icon className="h-8 w-8 text-muted-foreground" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold">{stat.value}</p>
              </div>
            </div>

            {stat.details.length > 0 && (
              <div className="mt-4 space-y-1">
                {stat.details.map((detail, detailIndex) => (
                  <div
                    key={detailIndex}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-muted-foreground">
                      {detail.label}
                    </span>
                    <span className={detail.color}>{detail.value}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default JobStatsCards;
